import {type Node, type Edge} from 'reactflow';

import {LayoutSettings} from 'ui-library/reactflow/layout/calculateDimensionsForLayout';

import {EntityMap_entityMap_EntityMapNode_entities as EntityMapEntityNodeOrInaccessibleInfoNode} from './__generated/EntityMap';
// Border: 2px
// Padding: 16px
// Row-gap: 4px
// Icon: 28px
// Schema Name: 18px minimum when single line of text
// Schema Name: 36px maximum when two lines of text
// Total: 68px - 86px, assume the maximum
const DEFAULT_NODE_HEIGHT = 86;
// Max-width of EntityNode is 200px, so assume the maximum
const DEFAULT_NODE_WIDTH = 200;

export const DEFAULT_FIT_VIEW_SETTINGS = {maxZoom: 1, duration: 800};

export const DEFAULT_LAYOUT_DEBOUNCE_MS = 500;

/**
 * Default horizontal and vertical spacing for the entity map.
 */
export const DEFAULT_ENTITY_MAP_LAYOUT_SETTINGS: Partial<
  LayoutSettings<Node<EntityMapEntityNodeOrInaccessibleInfoNode>, Edge>
> = {
  calculateNodeDimensions: (node) => ({
    height: node.height ?? DEFAULT_NODE_HEIGHT,
    width: node.width ?? DEFAULT_NODE_WIDTH,
  }),
  horizontalSpaceBetweenNodes: 300,
  verticalSpaceBetweenNodes: 10,
};

export const INITIAL_DEPTH_ON_LOAD = 3;

/**
 * The limit of nodes that should be displayed in an entity map.
 *
 * We disable expanding the entity map from handles if the current total number of nodes exceeds this limit.
 * Note that this means that if we have a graph with ENTITY_MAP_TOTAL_NODE_LIMIT - 1 nodes, we're allowed to
 * further expand the entity map from handles, and get to a graph with as many as ENTITY_MAP_TOTAL_NODE_LIMIT
 * plus ENTITY_MAP_EDGES_PER_NODE_LIMIT nodes.
 */
export const ENTITY_MAP_TOTAL_NODE_LIMIT = 1000;

/**
 * The maximum number of entity links an entity can have for us to load it.
 *
 * This is a limit on the number of nodes that could be linked to (i.e. two separate links from A to B would
 * only count for one).
 *
 * Note that this does not prohibit a node from being loaded and displayed if it has more than this number of
 * entity links pointing to it, it only prevents us from further expanding the map for this entity.
 *
 * E.g. A -> B[0...10] -> C, where C is linked to 10 B entities, and thousands more not part of the graph. In
 * that case, we would display C in the map, but not load any of C's other edges. The same would apply if
 * there were more than this limit of incoming edges for C.
 */
export const ENTITY_MAP_EDGES_PER_NODE_LIMIT = 50;

/**
 * The maximum number of additionally expanded entity IDs that can be sent to the backend.
 * Must match ENTITY_MAP_ADDITIONAL_EXPANDED_LIMIT in the backend.
 */
export const ENTITY_MAP_ADDITIONAL_EXPANDED_LIMIT = 50;
